import { create } from 'zustand';
import fetchService from '../services/fetchService';
import { logger, createFileLogger, checkAndEnableAdminConsole } from '../utils/logger';
import { UserPlan, UsageLimitsResponse, ParsedUsageData, BackendUserSchema, OnboardingState, OnboardingFlagKey } from '../types/user-schema';

import { API_BASE_URL } from '../api/config';
import { planConfigService } from '../services/planConfigService';
import { PLAN_RANKS } from '../config/planConfig';
import { requestCacheService } from '../services/requestCacheService';
import { billingService, MonthlyBill } from '../services/billing/billingService';
import { parseJwtPayload } from '../utils/jwtUtils';

interface UserState {
  auth0Id: string | null;
  plan: UserPlan | null;
  usage: UsageLimitsResponse | null;
  profile: BackendUserSchema | null;
  isLoading: boolean;
  error: string | null;
  
  // Billing data (global, not per-component)
  currentBill: MonthlyBill | null;
  billLastFetched: number | null;
  billFetchAttempted: boolean;  // True once the first fetch attempt starts — prevents perpetual spinner on error
  previousOverage: number | null;  // Track previous overage to detect when user enters pay-as-you-go
  // Bumped whenever fetchCurrentBill completes — org billing components subscribe to trigger re-fetch
  orgBillingLastRefreshed: number | null;
  
  // ✅ PERFORMANCE: Admin status (global, cached)
  isAdmin: boolean | null;
  adminLastChecked: number | null;
  
  // Invite-a-friend modal trigger
  showInviteFriendModal: boolean;
  setShowInviteFriendModal: (show: boolean) => void;

  // Avatar URL cache (stable reference to prevent image reloads)
  avatarUrl: string | null;
  avatarUrlCacheTime: number | null;
  
  // DEPRECATED: Integration status now checked directly from entityStore
  // Kept for backward compatibility only
  integrationStatus: {
    email: boolean;
    calendar: boolean;
    googleAnalytics: boolean;
    googleDrive: boolean;
  } | null;
  integrationStatusLastFetched: number | null;
  
  // Actions
  setAuth0Id: (id: string) => void;
  setPlan: (plan: UserPlan) => void;
  setUsage: (usage: UsageLimitsResponse) => void;
  setProfile: (profile: BackendUserSchema) => void;
  setOnboardingFlag: (key: OnboardingFlagKey, value: boolean) => Promise<void>;
  fetchUsage: () => Promise<void>;
  fetchProfile: () => Promise<void>;
  fetchCurrentBill: (opts?: { force?: boolean }) => Promise<void>;
  fetchAdminStatus: () => Promise<void>;
  fetchIntegrationStatus: () => Promise<void>;  // Integration status cache
  refreshIntegrationStatus: () => Promise<void>;  // Force refresh integration status
  
  // Getters
  getUsername: () => string;
  getVoiceProfile: () => any | null;
  
  // Voice profile actions
  refreshProfile: () => Promise<void>;
  
  // Debug helpers
  debugStore: () => void;
  getDebugInfo: () => {
    auth0Id: string | null;
    plan: UserPlan | null;
    usage: UsageLimitsResponse | null;
    profile: BackendUserSchema | null;
    hasToken: boolean;
    tokenExpiry: string | null;
  };
}

const log = createFileLogger('USER_STORE');
log.info('Creating store...');

// ✅ Lock to prevent concurrent fetchProfile() calls
// This adds explicit deduplication on top of requestCacheService
let profileFetchInProgress: Promise<any> | null = null;

const useUserStore = create<UserState>((set, get) => ({
  auth0Id: null,
  plan: null,
  usage: null,
  profile: null,
  isLoading: false,
  error: null,
  
  // Billing state
  currentBill: null,
  billLastFetched: null,
  billFetchAttempted: false,
  previousOverage: null,
  orgBillingLastRefreshed: null,
  
  // ✅ PERFORMANCE: Admin state
  isAdmin: null,
  adminLastChecked: null,
  
  // Invite-a-friend modal trigger
  showInviteFriendModal: false,
  setShowInviteFriendModal: (show: boolean) => set({ showInviteFriendModal: show }),

  // Avatar URL cache
  avatarUrl: null,
  avatarUrlCacheTime: null,
  
  // Integration status cache
  integrationStatus: null,
  integrationStatusLastFetched: null,

  setAuth0Id: (id: string) => {
    log.info('Setting auth0Id:', id);
    set({ auth0Id: id });
    // Fetch usage and profile when auth0Id is set
    const state = get();
    state.fetchUsage();
    state.fetchProfile();
  },

  setPlan: (plan: UserPlan) => {
    log.info('Setting plan:', plan);
    set({ plan });
  },

  setUsage: (usage: UsageLimitsResponse) => {
    log.info('Setting usage:', usage);
    set({ usage });
  },

  setOnboardingFlag: async (key: OnboardingFlagKey, value: boolean) => {
    // Optimistic update — UI reacts immediately
    set(state => ({
      profile: state.profile ? {
        ...state.profile,
        skeleton: {
          ...state.profile.skeleton,
          onboardingState: {
            ...state.profile.skeleton.onboardingState,
            [key]: value,
          }
        }
      } : null
    }) as Partial<UserState>);
    // Background write to backend — merge with current state to avoid overwriting other flags
    const current = get().profile?.skeleton?.onboardingState ?? {};
    try {
      const { userService } = await import('../services/users/userService');
      await userService.updateUserProfile({
        onboardingState: { ...current, [key]: value }
      });
    } catch (error) {
      log.error('[UserStore] Failed to persist onboarding flag:', key, error);
    }
  },

  setProfile: (profile: BackendUserSchema) => {
    log.info('Setting profile:', profile);
    
    // Generate and cache avatar URL if avatar exists
    let avatarUrl = null;
    if (profile?.skeleton?.avatar) {
      const avatar = profile.skeleton.avatar;
      // Only cache if it's an entity ID
      if (avatar.match(/^[0-9a-fA-F]{24}$/)) {
        const { FILES_ENDPOINTS } = require('../endpoints');
        avatarUrl = FILES_ENDPOINTS.read(avatar, undefined, 'full');
      } else if (avatar.startsWith('http://') || avatar.startsWith('https://')) {
        avatarUrl = avatar;
      }
    } else if (profile?.skeleton?.auth0?.picture) {
      avatarUrl = profile.skeleton.auth0.picture;
    }
    
    set({ 
      profile,
      avatarUrl,
      avatarUrlCacheTime: avatarUrl ? Date.now() : null
    });
    
    // Check if user is admin and enable console logs in production (verified by backend)
    checkAndEnableAdminConsole();
  },

  fetchProfile: async () => {
    // ✅ FIX: Explicit lock to prevent concurrent fetches
    // Production logs show requestCacheService isn't preventing concurrent calls (82ms apart)
    // This explicit lock ensures only ONE fetch happens at a time
    if (profileFetchInProgress) {
      log.info('[UserStore] Profile fetch already in progress, waiting for existing fetch...');
      try {
        return await profileFetchInProgress;
      } catch (error) {
        // If existing fetch failed, clear lock and let this request try
        log.warn('[UserStore] Existing fetch failed, will retry');
        profileFetchInProgress = null;
      }
    }
    
    // ✅ FIX: Skip profile fetch if user is not authenticated
    // This prevents 403/401 errors when viewing public content
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      log.info('[UserStore] Skipping profile fetch - user not authenticated');
      return;
    }
    
    try {
      // Create promise that covers the full fetch + store update so concurrent
      // waiters (line 195) don't resolve before set({ profile }) has been called.
      profileFetchInProgress = (async () => {
        log.info('[UserStore] Starting profile fetch (lock acquired)');
        
        // Use request cache to deduplicate simultaneous calls from multiple components
        const response = await requestCacheService.getOrFetch(
          'user-profile',
          () => fetchService(`${API_BASE_URL}/user/profile`, {
            credentials: 'include',
          })
        );

        log.info('Fetched user profile:', response);
      
      // 🔥 CRITICAL: Check if we got email from backend
      const profileEmail = response?.skeleton?.email || response?.email;
      if (profileEmail) {
        log.info('✅ [MIXPANEL EMAIL CHECK] Profile has email:', profileEmail);
        
        // 🔥 SYNC EMAIL AND PLAN DATA TO MIXPANEL IMMEDIATELY
        const { default: userTrackingService } = await import('../services/users/userTrackingService');
        const { useDataspaceContextStore } = await import('./dataspaceContextStore');
        
        log.info('🔄 [MIXPANEL EMAIL CHECK] Syncing email and plan data to Mixpanel...');
        
        // Get org data to calculate plan
        const orgs = useDataspaceContextStore.getState().userOrgs || [];
        const userId = response?.auth0Id || response?.id;
        
        let highestPlan = 'free';
        let paidOrgsCount = 0;
        let ownsPaidOrg = false;
        
        for (const org of orgs) {
          const orgPlan = org.skeleton?.billing_plan || 'free';
          const orgOwner = org.skeleton?.owner_user_id;
          
          if (orgPlan !== 'free') {
            paidOrgsCount++;
            if (orgOwner === userId) {
              ownsPaidOrg = true;
            }
          }
          
          if ((PLAN_RANKS[orgPlan] ?? 0) > (PLAN_RANKS[highestPlan] ?? 0)) {
            highestPlan = orgPlan;
          }
        }
        
        const personalPlan = response?.skeleton?.plan?.plan || 'free';
        const isPaidUser = paidOrgsCount > 0;
        
        userTrackingService.updateUserInfo({
          email: profileEmail,
          name: response?.skeleton?.name || response?.name,
          userId: userId,
          plan: highestPlan,
          personalPlan: personalPlan,
          isPaidUser: isPaidUser,
          ownsPaidOrg: ownsPaidOrg,
          paidOrgsCount: paidOrgsCount
        });
        log.info('✅ [MIXPANEL EMAIL CHECK] Email and plan data synced to Mixpanel!', {
          plan: highestPlan,
          isPaidUser,
          paidOrgsCount
        });
      } else {
        log.warn('⚠️ [MIXPANEL EMAIL CHECK] Profile has NO email! Response:', {
          hasEmail: !!response?.email,
          hasSkeletonEmail: !!response?.skeleton?.email,
          skeletonKeys: response?.skeleton ? Object.keys(response.skeleton) : []
        });
      }
      
      // Generate and cache avatar URL
      let avatarUrl = null;
      if (response?.skeleton?.avatar) {
        const avatar = response.skeleton.avatar;
        // Only cache if it's an entity ID
        if (avatar.match(/^[0-9a-fA-F]{24}$/)) {
          const { FILES_ENDPOINTS } = require('../endpoints');
          avatarUrl = FILES_ENDPOINTS.read(avatar, undefined, 'full');
        } else if (avatar.startsWith('http://') || avatar.startsWith('https://')) {
          avatarUrl = avatar;
        }
      } else if (response?.skeleton?.auth0?.picture) {
        avatarUrl = response.skeleton.auth0.picture;
      }
      
      set({ 
        profile: response,
        avatarUrl,
        avatarUrlCacheTime: avatarUrl ? Date.now() : null
      });
      
      // Check if user is admin and enable console logs in production (verified by backend)
      checkAndEnableAdminConsole();
      })();
      await profileFetchInProgress;
    } catch (error) {
      log.error('Error fetching profile:', error);
      // Don't set error state for profile fetch failures since we have fallbacks
    } finally {
      // ✅ CRITICAL: Clear lock after fetch completes (success or failure)
      // This ensures subsequent calls can proceed
      profileFetchInProgress = null;
      log.info('[UserStore] Profile fetch complete (lock released)');
    }
  },

  fetchUsage: async () => {
    // ✅ FIX: Skip usage fetch if user is not authenticated
    // This prevents 403/401 errors when viewing public content
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      log.info('[UserStore] Skipping usage fetch - user not authenticated');
      return;
    }
    
    if (get().isLoading) return;
    
    set({ isLoading: true, error: null });
    
    try {
      // Use request cache to deduplicate simultaneous calls from multiple components
      const response = await requestCacheService.getOrFetch(
        'user-usage',
        () => fetchService(`${API_BASE_URL}/user/usage`, {
          credentials: 'include',
        })
      );
      
      log.info('Raw usage response:', response);
      
      const parsedData = parseUsageData(response);
      log.info('Parsed usage data:', parsedData);
      
      // 🔧 NEW: Auto-fix null plan limits
      const { autoFixUserLimitsIfNeeded } = await import('../utils/fixUserLimits');
      await autoFixUserLimitsIfNeeded(response);
      
      set({ 
        plan: parsedData.plan,
        usage: parsedData.usage,
        isLoading: false 
      });
    } catch (error) {
      log.error('Error fetching usage:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch usage data',
        isLoading: false 
      });
    }
  },

  // Fetch billing data (global, deduplicated, cached)
  fetchCurrentBill: async (opts?: { force?: boolean }) => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      log.info('[UserStore] Skipping billing fetch - user not authenticated');
      return;
    }
    
    if (get().isLoading) return;
    
    const now = Date.now();
    const lastFetch = get().billLastFetched;
    
    // Cache for 60 seconds unless caller requests a forced refresh (e.g. billing_refresh command)
    if (!opts?.force && lastFetch && (now - lastFetch < 60000)) {
      log.info('[BILLING] Using cached billing data', {
        age: `${now - lastFetch}ms`,
        cacheDuration: '60000ms'
      });
      return;
    }
    
    try {
      log.info('[BILLING] Fetching current bill from backend...');
      set({ billFetchAttempted: true });

      const previousOverage = get().previousOverage;
      const previousPlan = get().plan;
      
      // Force refresh bypasses the 60s cache and request dedup (triggered by billing_refresh command)
      if (opts?.force) {
        set({ billLastFetched: null });
        requestCacheService.invalidate('current-bill');
      }

      const currentBill = await requestCacheService.getOrFetch(
        'current-bill',
        () => billingService.getCurrentBill()
      );
      
      // Check if user just entered overage (spent past their included budget)
      const currentOverage = currentBill?.charges?.overageCharge || 0;
      const isPaidPlan = previousPlan?.plan && previousPlan.plan !== 'free' && previousPlan.plan !== 'payg';
      const justEnteredOverage = isPaidPlan &&
                                 previousOverage === 0 &&
                                 currentOverage > 0 &&
                                 previousOverage !== null;

      if (justEnteredOverage) {
        const budgetCents = currentBill?.plan?.monthlyFreeCredits || 0;
        log.info('[BILLING] 💳 User exceeded included budget', {
          previousOverage,
          currentOverage,
          budgetCents,
        });

        if (budgetCents > 0) {
          try {
            const { toastService } = await import('../services/ui/toastService');
            toastService.info(
              `You've used your $${(budgetCents * 0.01).toFixed(2)} included budget. Additional usage will be charged at prevailing API pricing.`,
              { position: 'top-center', duration: 8000 }
            );
          } catch (err) {
            log.warn('[BILLING] Failed to show overage notification:', err);
          }
        }
      }
      
      set({ 
        currentBill, 
        billLastFetched: now,
        previousOverage: currentOverage,
        orgBillingLastRefreshed: now,
      });

      // // Trigger invite-a-friend modal for free users who've consumed 100+ credits (once)
      // const isFreePlan = currentBill?.plan?.name === 'free';
      // const creditsUsed = currentBill?.charges?.usedCost ?? 0;
      // const alreadyShown = localStorage.getItem('inviteFriendShown') === 'true';
      // if (isFreePlan && creditsUsed >= 100 && !alreadyShown) {
      //   set({ showInviteFriendModal: true });
      // }

      log.info('[BILLING] ✅ Current bill fetched successfully');
    } catch (error) {
      log.error('[BILLING] Error fetching current bill:', error);
      // Don't set error state - billing is not critical to app functionality
    }
  },

  // ✅ PERFORMANCE: Fetch admin status (global, cached)
  fetchAdminStatus: async () => {
    // ✅ FIX: Skip admin status check if user is not authenticated
    // This prevents 403/401 errors when viewing public content
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      log.info('[UserStore] Skipping admin status check - user not authenticated');
      return;
    }
    
    const now = Date.now();
    const lastCheck = get().adminLastChecked;
    
    // Cache for 5 minutes - admin status rarely changes
    if (lastCheck && (now - lastCheck < 300000)) {
      log.info('[ADMIN] Using cached admin status', {
        isAdmin: get().isAdmin,
        age: `${now - lastCheck}ms`,
        cacheDuration: '300000ms'
      });
      return;
    }
    
    try {
      log.info('[ADMIN] Checking admin access from backend...');
      
      // Use request cache to deduplicate simultaneous calls
      const response = await requestCacheService.getOrFetch(
        'admin-check',
        () => fetchService(`${API_BASE_URL}/admin/check-access`, {
          credentials: 'include',
        })
      );
      
      const isAdmin = response.is_admin === true;
      
      set({ 
        isAdmin,
        adminLastChecked: now
      });
      
      log.info('[ADMIN] Admin status updated:', { isAdmin });
      
      // If admin, enable console logs
      if (isAdmin) {
        checkAndEnableAdminConsole();
      }
    } catch (error) {
      log.error('[ADMIN] Error checking admin status:', error);
      // Default to false on error
      set({ 
        isAdmin: false,
        adminLastChecked: now
      });
    }
  },

  getUsername: (): string => {
    const { profile } = get();
    
    // Try to get from user profile first
    if (profile?.skeleton) {
      const name = profile.skeleton.name || 
                  profile.skeleton.auth0?.name || 
                  profile.skeleton.email || 
                  profile.skeleton.auth0?.email;
      
      if (name) return name;
    }
    
    // Fallback to JWT token parsing
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        
        const payload = parseJwtPayload(token);
        if (payload) {
          return payload.name || payload.email || 'Unknown User';
        }
      } catch (tokenError) {
        log.warn('[UserStore] Error parsing JWT token:', tokenError);
      }
    }
    
    return 'Unknown User';
  },

  getVoiceProfile: () => {
    const { profile } = get();
    
    if (profile?.skeleton?.voiceProfile) {
      log.info('[UserStore] ✅ Returning cached voice profile from userStore');
      return profile.skeleton.voiceProfile;
    }
    
    log.info('[UserStore] No voice profile in cache, returning null');
    return null;
  },

  refreshProfile: async () => {
    log.info('[UserStore] Refreshing profile (forced re-fetch)');
    
    // Clear the request cache for user-profile to force fresh fetch
    requestCacheService.invalidate('user-profile');
    
    // Fetch profile again
    await get().fetchProfile();
    
    log.info('[UserStore] ✅ Profile refreshed successfully');
  },
  
  // Debug helper function to log the current state
  debugStore: () => {
    const { auth0Id, plan, usage, profile } = get();
    const token = localStorage.getItem('authToken');
    
    logger.group('🔍 UserStore Debug Info');
    log.log('auth0Id:', auth0Id);
    log.log('plan:', plan);
    log.log('usage:', usage);
    log.log('profile:', profile);
    log.log('hasToken:', !!token);
    
    if (token) {
      try {
        // Try to parse the token to get the expiry time
        
        const payload = parseJwtPayload(token);
        if (payload) {
          log.log('token.exp:', new Date(payload.exp * 1000).toLocaleString());
          log.log('token expiry in:', (payload.exp * 1000 - Date.now()) / 1000 / 60, 'minutes');
        }
      } catch (error) {
        log.error('Error parsing token:', error);
      }
    }
    
    logger.groupEnd();
  },
  
  // Return debug info for UI display
  getDebugInfo: () => {
    const { auth0Id, plan, usage, profile } = get();
    const token = localStorage.getItem('authToken');
    let tokenExpiry = null;
    
    if (token) {
      try {
        
        const payload = parseJwtPayload(token);
        if (payload) {
          tokenExpiry = new Date(payload.exp * 1000).toLocaleString();
        }
      } catch {
        tokenExpiry = 'Error parsing token';
      }
    }
    
    return {
      auth0Id,
      plan,
      usage,
      profile,
      hasToken: !!token,
      tokenExpiry
    };
  },
  
  fetchIntegrationStatus: async () => {
    // If already loaded, skip
    const current = get().integrationStatus;
    if (current !== null) {
      log.log('[fetchIntegrationStatus] ✅ Already loaded, skipping');
      return;
    }
    
    log.log('[fetchIntegrationStatus] 🔄 Fetching integration status...');
    
    try {
      // Use IntegrationSystem to check for entity-based integrations
      const { IntegrationSystem } = await import('../services/integrations/core/IntegrationSystem');
      const integrationSystem = IntegrationSystem.getInstance();
      
      // Get all user's integrations
      const integrations = await integrationSystem.getUserIntegrations('');
      
      // Check which integrations are connected
      const status = {
        email: integrations.some(i => 
          i.skeleton.integrationType === 'Gmail' && 
          i.skeleton.status === 'connected'
        ),
        calendar: integrations.some(i => 
          i.skeleton.integrationType === 'GoogleCalendar' && 
          i.skeleton.status === 'connected'
        ),
        googleAnalytics: integrations.some(i => 
          i.skeleton.integrationType === 'GoogleAnalytics' && 
          i.skeleton.status === 'connected'
        ),
        googleDrive: integrations.some(i =>
          i.skeleton.integrationType === 'GoogleDrive' &&
          i.skeleton.status === 'connected'
        )
      };
      
      log.log('[fetchIntegrationStatus] ✅ Fetched from entities:', status);
      
      set({
        integrationStatus: status,
        integrationStatusLastFetched: Date.now()
      });
    } catch (error) {
      log.error('[fetchIntegrationStatus] ❌ Error:', error);
      // Fail safe - assume all disconnected
      set({
        integrationStatus: { email: false, calendar: false, googleAnalytics: false, googleDrive: false },
        integrationStatusLastFetched: Date.now()
      });
    }
  },
  
  refreshIntegrationStatus: async () => {
    log.log('[refreshIntegrationStatus] 🔄 Force refreshing...');
    
    // Clear cache
    set({ integrationStatus: null, integrationStatusLastFetched: null });
    
    // Fetch fresh
    await get().fetchIntegrationStatus();
  }
}));

// Helper function to parse the backend response into our frontend format
function parseUsageData(backendResponse: any): ParsedUsageData {
  const log = createFileLogger('USER_STORE');
  log.info('Parsing backend response:', backendResponse);
  
  const skeleton = backendResponse.skeleton || {};
  const rawPlan = skeleton.plan;
  
  // Handle empty dict from backend (plan defaults to {} if not set)
  // If plan is missing or invalid, default to free plan
  const plan = rawPlan && typeof rawPlan === 'object' && Object.keys(rawPlan).length > 0 && rawPlan.track && rawPlan.plan
    ? rawPlan
    : {
        track: 'cloud',
        plan: 'free',
        activatedAt: new Date().toISOString()
      };
  
  log.info('📊 Plan extraction debug:', {
    hasSkeleton: !!backendResponse.skeleton,
    skeletonKeys: Object.keys(skeleton),
    rawPlan: rawPlan,
    planValue: plan,
    planType: typeof plan,
    hasTrack: plan?.track,
    hasPlanName: plan?.plan,
    isEmpty: rawPlan && Object.keys(rawPlan).length === 0
  });
  
  // NEW: Check if user has the new usage structure
  if (skeleton.usage && skeleton.currentPlanLimits) {
    log.info('Using new usage structure');
    
    // Build UsageLimitsResponse from new structure
    const usage: UsageLimitsResponse = {
      has_limits: Object.values(skeleton.currentPlanLimits).some((limit: any) => limit !== -1),
      within_limits: !skeleton.overage || Object.values(skeleton.overage).every((overage: any) => overage === 0),
      plan_type: plan?.plan || 'free',
      exceeded_limits: skeleton.overage ? Object.keys(skeleton.overage).filter((key: string) => (skeleton.overage as any)[key] > 0) : [],
      limits: {},
      usage: skeleton.usage,
      overage: skeleton.overage || {}
    };
    
    // Build limits detail
    const usageTypes = ['entities', 'toolCalls', 'userMessages', 'inputTokens', 'outputTokens', 'totalTokens', 'acceptedEdits'];
    for (const type of usageTypes) {
      if (skeleton.usage[type] !== undefined) {
        const used = skeleton.usage[type] || 0;
        const max = skeleton.currentPlanLimits[type] || 0;
        const overage = skeleton.overage ? skeleton.overage[type] || 0 : 0;
        
        usage.limits[type] = {
          used,
          max,
          exceeded: max !== -1 && used >= max,
          overage
        };
      }
    }
    
    return { plan, usage };
  }
  
  // LEGACY: Fall back to old query_count structure if new structure not available
  log.info('Using legacy structure fallback');
  
  const planLimits = plan ? planConfigService.getCachedPlan(plan.plan)?.limits : null;
  
  if (planLimits) {
    const usage: UsageLimitsResponse = {
      has_limits: true,
      within_limits: true, // Will be calculated
      plan_type: plan?.plan || 'free',
      exceeded_limits: [],
      limits: {
        entities: {
          used: 0, // Would need actual count
          max: planLimits.entities,
          exceeded: false,
          overage: 0
        },
        toolCalls: {
          used: skeleton.query_count || 0,
          max: planLimits.toolCalls,
          exceeded: (skeleton.query_count || 0) >= planLimits.toolCalls,
          overage: Math.max(0, (skeleton.query_count || 0) - planLimits.toolCalls)
        },
        userMessages: {
          used: 0, // Would need actual count
          max: planLimits.userMessages,
          exceeded: false,
          overage: 0
        }
      },
      usage: {
        entities: 0,
        toolCalls: skeleton.query_count || 0,
        userMessages: 0,
        inputTokens: 0,
        outputTokens: 0,
        totalTokens: 0,
        acceptedEdits: 0
      },
      overage: {
        entities: 0,
        toolCalls: Math.max(0, (skeleton.query_count || 0) - planLimits.toolCalls),
        userMessages: 0,
        inputTokens: 0,
        outputTokens: 0,
        totalTokens: 0,
        acceptedEdits: 0
      }
    };
    
    // Calculate within_limits and exceeded_limits
    const exceeded = Object.entries(usage.limits).filter(([_, limit]) => limit.exceeded).map(([key, _]) => key);
    usage.exceeded_limits = exceeded;
    usage.within_limits = exceeded.length === 0;
    
    return { plan, usage };
  }
  
    return { plan, usage: null };
}

export default useUserStore; 